/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kBrowserObjects - the browser data classes kObject, kObjectArray, kBoolean and kTime.
 *
 * MOVED HERE FROM kalmstromShared4 ON 2026-10-14, unchanged (owner decision "Decouple it properly"):
 * kEditableTable, kHTML, kContextMenu, kSearch and kExcelExport import them, and taking them from
 * kalmstromShared4 meant loading kalmstromShared4 - whose module scope runs SetCoreUtils(ks) and so
 * replaced a non-SharePoint host's own error reporter. kalmstromShared4 RE-EXPORTS all four, so every
 * existing `import { kObject } from ".../kalmstromShared4"` gets the very same classes.
 *
 * `ks` here is kBrowserShared (the host's registered handler), never kalmstromShared4.
 */
import ks from "./kBrowserShared";
import { kObjectCore } from "../kReusableCoreTS/kCoreObjects";
import { kSearchItemsCore } from "../kReusableCoreTS/kSearchCore";
import { kTimeCore } from "../kReusableCoreTS/kCoreTime";
import { kHTMLTable, kHTMLRow } from "./kHTML";


// ⛔ kObject MUST extend kObjectCore DIRECTLY. Do not insert a class between them.
//
// The contract is STRUCTURAL and runs the OPPOSITE way to inheritance: kObjectCore must stay
// assignable TO kObject, because kHTML and kEditableTable take a kObjectCore off a
// kObjectArrayCore.Items and hand it to a kObject parameter. That is why kObjectCore carries
// stubs for ShowProgress / ToRow / FromRow / ToAnchor it never uses. Anything kObject gains
// that kObjectCore lacks breaks it instantly.
//
// TRIED AND REVERTED 2026-08-22: `kObject extends kSearchItemCore` - so browser objects would
// inherit the new search-text builder. It failed exactly as the 2026-07-30 note predicted:
// "kHTML.ts:1193 error TS2740: Type 'kObjectCore' is missing the following properties from
// type 'kObject': SearchFields, _ExtraSearchText, BuildSearchableString, AddSearchText".
// "It only ADDS members" is not sufficient here - adding to kObject is what breaks it.
// If a browser-only class ever needs the builder, the members must go on kObjectCore itself
// (where matching already lives, for this same reason), NOT on a class in between.
export class kObject extends kObjectCore {
    // Browser-specific methods only — Title, ID, Index, UniqueID, constructor,
    // ToJSONObj, FromSaved, CloneObject are inherited from kObjectCore.

    public ShowProgress = (
        ProgressMessage: string,
        Done: number,
        Total: number
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            ks.ShowNotification(
                ProgressMessage + " " + Done.toString() + "/" + Total.toString()
            );
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObject.ShowProgress");
            return;
        }
    };
    public ToRow = (): HTMLTableRowElement | null => {
        if (ks.KErrorMode === true) {return null;}
        try {
            const Row: kHTMLRow = new kHTMLRow();
            Row.AddCell(ks.Nz(this.Title));
            return Row.Row;
        } catch (e) {ks.KGlobalErrorHandler(e, "kObject.ToRow");return null;}
    };
    public FromRow = (CurrRow: kHTMLRow): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            CurrRow.GetCells();
            if (CurrRow.Cells.length === 1) {
                this.Title = ks.Nz(CurrRow.Cells[0].textContent).trim();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObject.FromRow");
        }
    };
    public ToAnchor = (): HTMLAnchorElement  => {
        if (ks.KErrorMode === true) {return document.createElement("a");}
        try {
            const Anchor: HTMLAnchorElement = document.createElement("a");
            Anchor.textContent = ks.Nz(this.Title);
            return Anchor;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObject.ToAnchor");
            return document.createElement("a");
        }
    }
}
// Extends kSearchItemsCore, NOT kObjectArrayCore directly. kSearchItemsCore is
// kObjectArrayCore plus the DOM-free search engine (Search / SearchAllWords /
// GetHighlightedText / NonHidden), so every browser collection inherits it and
// kSearchItems is once again just "kObjectArray + DOM".
// Purely additive: nothing is removed and Items is NOT narrowed.
//
// The COLLECTION side is the only side with a class to extend. kObject above extends
// kObjectCore DIRECTLY, not kSearchItemCore - the per-item search state
// (SearchableString / FilteredOut / CheckMatch) was moved onto kObjectCore itself on
// 2026-07-30, which left kSearchItemCore an empty marker. This comment used to say
// "the same reason kObject extends kSearchItemCore"; it never did, and reading that
// sentence is how kSearchItemCore gets re-proposed as a base class that carries
// something. It does not. See the chain diagram at the top of kSearchCore.ts.
export class kObjectArray extends kSearchItemsCore {
    // Items and NewItem inherited from kObjectArrayCore as kObjectCore[]
    // Do NOT override with kObject[] — it breaks subclasses whose items extend kObjectCore via core chains
    // Inherited from kObjectArrayCore: Items, NewItem, ExtendedClass,
    // push, RemoveItemByIndex, RemoveItemByTitle, RemoveItemByUniqueID,
    // RemoveItemByID, GetItemByTitle, GetOrAddByTitle, GetItemByUniqueID,
    // GetItemByID, GetRandomItem, SortByTitle, SortByProperty,
    // SortAndRemoveDoubles, RemoveTitleDoubles, ToTitlesArray,
    // ToPersistedArray, FromPersistedArray, FromTitlesArray

    // Browser-specific override: uses ks.AddDelayedAction instead of setTimeout,
    // and calls ShowProgress on items for UI feedback.
    // eslint-disable-next-line @typescript-eslint/ban-types
    public CallFunctionOnEach = (
        FunctionName: any,
        AllDone: any,
        ObjectName?: any,
        StartIndex?: number,
        ProgressMessage?: string,
        ProgressCallback?: any,
        Parameter?:any
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this._ObjectName = ks.Nz(ObjectName);
            this._FunctionName = FunctionName;
            this._ActionDoneCallback = AllDone;
            this._ForEachPar = Parameter;
            if (ProgressCallback) {
                this._ProgressCB = ProgressCallback;
            }
            if (ks.KNotNullOrUndefined(StartIndex)) {
                this._ActionCounter = ks.N0(StartIndex);
            } else {
                this._ActionCounter = 0;
            }
            this._ProgressMessage = ks.Nz(ProgressMessage);
            this._NextCallback();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObjectArray.CallFunctionOnEach");
        }
    };
    protected _NextCallback = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this._ActionCounter < this.Items.length) {
                this._ActionCounter++;
                if (this._ProgressCB) {
                    this._ProgressCB(
                        this._ProgressMessage,
                        this._ActionCounter,
                        this.Items.length
                    );
                } else {
                    if (ks.KNotEmptyString(this._ProgressMessage)) {
                        (this.Items[this._ActionCounter - 1] as kObject).ShowProgress(
                            this._ProgressMessage,
                            this._ActionCounter,
                            this.Items.length
                        );
                    }
                }
                const currentItem = this.Items[this._ActionCounter - 1] as any; // Cast to any for dynamic property access
                if (ks.KNotEmptyString(this._ObjectName)) {
                    ks.AddDelayedAction("Avoid call stack issues",currentItem[this._ObjectName][this._FunctionName],"",5,this._NextCallback);
                } else {
                    ks.AddDelayedAction("Avoid call stack issues",currentItem[this._FunctionName],"",5,this._NextCallback);
                }
            } else {
                ks.DoCallBack(this._ActionDoneCallback);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObjectArray._NextCallback");
        }
    };

    // Browser-specific methods (DOM/HTML operations)
    public ToTableObject = (): kHTMLTable => {
        if (ks.KErrorMode === true) {return new kHTMLTable();}
        try {
            const RenderingTable: kHTMLTable = new kHTMLTable();
            for (const CurrItem of this.Items) {
                RenderingTable.AddRowObject(<HTMLTableRowElement>(CurrItem as any as kObject).ToRow());
            }
            return RenderingTable;
        } catch (e) {ks.KGlobalErrorHandler(e, "kObjectArray.ToTableObject");return new kHTMLTable();}
    };
    public ToTable = (): HTMLTableElement => {
        if (ks.KErrorMode === true) {
            return document.createElement("table");
        }
        try {
            const TableObj: kHTMLTable | null = this.ToTableObject();
            if (null !== TableObj) {
                return TableObj.Table;
            }
            return document.createElement("table");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObjectArray.ToTable");
            return document.createElement("table");
        }
    };
    public ToTableString = (WithCSS?:boolean): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            let ReturnString: string = "";
            if (this.Items.length > 0) {
                const TableObj: kHTMLTable | null = this.ToTableObject();
                if(WithCSS === true) {
                    ReturnString += "<style>";
                    ReturnString += kHTMLTable.GetStyle();
                    ReturnString += "</style>";
                }
                if (null !== TableObj) {
                    ReturnString += TableObj.Table.outerHTML;
                }
            }
            return ReturnString;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObjectArray.ToTableString");
            return "";
        }
    };
    public FromTable = (TableHTML: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const RenderingTable: kHTMLTable = new kHTMLTable();
            this.Items = [];
            if (ks.KNotEmptyHTML(TableHTML)) {
                RenderingTable.FromString(TableHTML);
                RenderingTable.GetRows();
                for (const CurrRow of RenderingTable.Rows) {
                    const NewObject: kObject = new this.NewItem() as any as kObject;
                    if (null !== NewObject) {
                        NewObject.FromRow(CurrRow);
                        if (ks.KNotNullOrUndefined(NewObject.ID)) {
                            NewObject.ID = this.Items.length;
                        }
                        this.push(NewObject);
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kObjectArray.FromTable");
        }
    };
}

export class kBoolean {
    public Value = false;
    public ID = "";
    public Caption = "";

    public constructor(ID: string, Value: boolean) {
        this.ID = ID;
        this.Value = Value;
    }

    public ToElements = (ParentObj: HTMLElement, Caption: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const DivContainer: HTMLDivElement = document.createElement("div");
            const HTMLCheckbox: HTMLInputElement =
                document.createElement("input");
            HTMLCheckbox.type = "checkbox";
            HTMLCheckbox.id = this.ID;
            if (this.Value) {
                HTMLCheckbox.setAttribute("checked", "checked");
            }
            HTMLCheckbox.name = this.ID;
            HTMLCheckbox.value = this.ID;
            DivContainer.appendChild(HTMLCheckbox);

            const Label: HTMLLabelElement = document.createElement("label");
            Label.htmlFor = this.ID;
            Label.textContent = Caption;
            DivContainer.appendChild(Label);
            ParentObj.appendChild(DivContainer);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBoolean.ToElements");
        }
    };
    public FromElements = (Doc?: Document): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (!ks.KNotNullOrUndefined(Doc)) {
                Doc = document;
            }
            if (undefined !== Doc) {
                const HTMLCheckbox: HTMLInputElement = Doc.getElementById(
                    this.ID
                ) as HTMLInputElement;
                if (ks.KNotNullOrUndefined(HTMLCheckbox)) {
                    this.Value = HTMLCheckbox.checked;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBoolean.FromElements");
        }
    };
}

export class kTime extends kTimeCore {
    // Inherited from kTimeCore: Year, Month, Day, Hour, Minute, Second, MilliSecond,
    // IsValid (defaults to false), isBefore, isWeekend, IsToday, DaysToOtherDate,
    // SetTimeToBeginningOfDay, SetTimeToEndOfDay, SetTimeFromString,
    // SetTimeToBeginningOfWeek, SetTimeToEndOfWeek, SetDayToFirstOfMonth,
    // SetDayToLastOfMonth, SetToNow, Add, AddDays, AddMinutes, SubractMinutes,
    // SubractDays, ToJsDate, TreatAsUTC, fromJSDate, fromISO, FromISODate,
    // FromISODateTime, FromJSON, FromSPFXDate, ToISODate, ToISOTime,
    // ToISODateTime, ToISODateTimeSecs, To2DigitTime, Weekday, WeekNumber,
    // YearWeekNumber, MSecsFromNow, SecondsFromNow, SecsFromNow, MinutesFromNow

    // Browser-specific overrides: use navigator.languages[0] instead of serverLocale()
    public WeekdayName = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            if(this.IsValid){
                const CurrDate: Date = this.ToJsDate();
                return CurrDate.toLocaleDateString(navigator.languages[0], {
                    weekday: "long"
                });
            }else{
                return "";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.WeekdayName");
            return "";
        }
    };
    public WeekDayName2 = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            if(this.IsValid){
                const CurrDate: Date = this.ToJsDate();
                const ShortWeekdayName:string = CurrDate.toLocaleDateString(navigator.languages[0], {weekday: "short"});
                return (ShortWeekdayName.substring(0,1).toUpperCase() + ShortWeekdayName.substring(1,2));
            }
            return "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.WeekDayName2");
            return "";
        }
    };
    public MonthName = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            const CurrDate: Date = this.ToJsDate();
            return CurrDate.toLocaleDateString(navigator.languages[0], {
                month: "long",
            });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.MonthName");
            return "";
        }
    };

    // Browser-specific override: empty locale defaults to navigator.languages[0]
    // (the user's preferred language) instead of serverLocale(). See kTimeCore.ToSmartLocalDate.
    // RecentDays: see kTimeCore.SmartLocalText (yesterday / weekday steps, opt-in).
    public ToSmartLocalDate = (Locale: string = "", RecentDays: boolean = false): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            if (!this.IsValid || this.Year <= 1970) {
                return "";
            }
            let UseLocale: string = Locale;
            if (UseLocale === "") {
                UseLocale = navigator.languages[0];
            }
            return kTimeCore.SmartLocalText(this.ToJsDate(), UseLocale, RecentDays);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.ToSmartLocalDate");
            return "";
        }
    };

    // Browser-specific override: preserves TimeZoneBias parameter for API compatibility
    public FromSPDate = (SPDate: string, AllDay?: boolean,TimeZoneBias?:number): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (!SPDate) {
                this.IsValid = false;
                return;
            }
            if (AllDay === true) {
                SPDate = SPDate.replace("T", " ").replace("Z", "");
            }
            const NewTimeJSDate: Date = new Date(SPDate);
            if (ks.KNotNullOrUndefined(NewTimeJSDate)) {
                this.fromJSDate(NewTimeJSDate);
                // if(TimeZoneBias){
                //     this.AddMinutes(TimeZoneBias + NewTimeJSDate.getTimezoneOffset());
                // }
            } else {
                this.IsValid = false;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.FromSPDate");
        }
    };

    // Browser-specific override: Clone returns browser kTime (not core kTime)
    public Clone = (): kTime => {
        if (ks.KErrorMode === true) {
            return new kTime();
        }
        try {
            const NewTime: kTime = new kTime();
            NewTime.Year = this.Year;
            NewTime.Month = this.Month;
            NewTime.Day = this.Day;
            NewTime.Hour = this.Hour;
            NewTime.Minute = this.Minute;
            NewTime.Second = this.Second;
            NewTime.MilliSecond = this.MilliSecond;
            NewTime.IsValid = this.IsValid;
            return NewTime;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.Clone");
            return new kTime();
        }
    };

    // The host's regional short-date pattern, falling back to ISO. A host that has not loaded a
    // regional format yet is an EXPECTED state, not an error - hence the plain default rather
    // than a report. This used to be an inline `try { } catch { //Ignore this error }` inside
    // ToLocalShortDate; explicit null/empty tests mean nothing has to be swallowed at all.
    private static _ShortDatePattern = (): string => {
        if (ks.KErrorMode === true) {
            return "yyyy-MM-dd";
        }
        try {
            if (null !== ks.NumberandDateFormat) {
                const Pattern: string = ks.Nz(ks.NumberandDateFormat.Df);
                if (ks.KNotEmptyString(Pattern)) {
                    return Pattern;
                }
            }
            return "yyyy-MM-dd";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime._ShortDatePattern");
            return "yyyy-MM-dd";
        }
    };

    // Browser-specific override: uses ks.NumberandDateFormat.Df for regional formatting
    public ToLocalShortDate = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            if (this.Year <= 1970) {
                return "";
            }
            return this._FormatShortDate(kTime._ShortDatePattern().toLowerCase());
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.ToLocalShortDate");
            return "";
        }
    };
    public ToLocalShortDateAndTime = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            if (this.Year > 1969) {
                return (
                    this.ToLocalShortDate() +
                    " " +
                    this.ToJsDate().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                    })
                );
            } else {
                return "";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kTime.ToLocalShortDateAndTime");
            return "";
        }
    };
}
